import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  Target, Flame, Thermometer, Snowflake, TrendingUp, Clock,
  Mail, Calendar, Users, RefreshCw, ChevronRight, Zap,
  Lightbulb, ArrowUpRight, Phone, Building, Star
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Priority colors and icons
const PRIORITY_CONFIG = {
  hot: {
    color: 'bg-red-500/20 text-red-400 border-red-500/30',
    bgColor: 'bg-red-500/10',
    icon: Flame,
    label: 'HOT LEAD',
    description: 'Contact now!'
  },
  warm: {
    color: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    bgColor: 'bg-orange-500/10',
    icon: Thermometer,
    label: 'WARM',
    description: 'Follow up soon'
  },
  cold: {
    color: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    bgColor: 'bg-blue-500/10',
    icon: Snowflake,
    label: 'COLD',
    description: 'Nurture more'
  }
};

const LeadPriorityQueue = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ hot: { count: 0 }, warm: { count: 0 }, cold: { count: 0 } });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  const fetchPriorityQueue = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const [queueRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/lead-scoring/priority-queue?limit=10`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/lead-scoring/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setLeads(queueRes.data.leads || []);
      setStats(statsRes.data.stats || {});
    } catch (error) {
      console.error('Failed to fetch priority queue:', error);
      toast.error('Failed to load lead priority queue');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchPriorityQueue();
  }, [fetchPriorityQueue]);

  const handleRefreshScores = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${BACKEND_URL}/api/lead-scoring/refresh-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('AI scores refreshed for all leads');
      await fetchPriorityQueue();
    } catch (error) {
      toast.error('Failed to refresh scores');
      setRefreshing(false);
    }
  };

  const handleSendEmail = (lead) => {
    // Navigate to inbox with compose
    navigate(`/inbox?compose=${lead.lead_email}`);
  };

  const handleBookMeeting = (lead) => {
    toast.success(`Meeting invite will be sent to ${lead.lead_name}`);
  };

  const handleAddToCampaign = (lead) => {
    navigate(`/campaigns?enroll=${lead.lead_id}`);
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-400';
    if (score >= 40) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getScoreRing = (score) => {
    const circumference = 2 * Math.PI * 40;
    const strokeDashoffset = circumference - (score / 100) * circumference;
    
    let color = '#ef4444'; // red
    if (score >= 70) color = '#22c55e'; // green
    else if (score >= 40) color = '#eab308'; // yellow
    
    return { strokeDashoffset, color };
  };

  if (loading) {
    return (
      <div data-testid="priority-queue-loading" className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-emerald-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div data-testid="lead-priority-queue-page" className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 md:mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Target className="w-7 h-7 md:w-8 md:h-8 text-emerald-400" />
            Lead Priority Queue
          </h1>
          <p className="text-emerald-300/70 text-sm md:text-base">AI-powered lead scoring and recommendations</p>
        </div>
        <Button
          onClick={handleRefreshScores}
          disabled={refreshing}
          className="bg-emerald-600 hover:bg-emerald-700 text-white w-full md:w-auto"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh AI Scores'}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-3 md:gap-6 mb-6 md:mb-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-red-500/10 border-red-500/30">
            <CardContent className="p-3 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-400 text-xs md:text-sm font-medium">Hot Leads</p>
                  <p className="text-2xl md:text-4xl font-bold text-white">{stats.hot?.count || 0}</p>
                </div>
                <Flame className="w-6 h-6 md:w-10 md:h-10 text-red-400" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="bg-orange-500/10 border-orange-500/30">
            <CardContent className="p-3 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-400 text-xs md:text-sm font-medium">Warm Leads</p>
                  <p className="text-2xl md:text-4xl font-bold text-white">{stats.warm?.count || 0}</p>
                </div>
                <Thermometer className="w-6 h-6 md:w-10 md:h-10 text-orange-400" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="p-3 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-400 text-xs md:text-sm font-medium">Cold Leads</p>
                  <p className="text-2xl md:text-4xl font-bold text-white">{stats.cold?.count || 0}</p>
                </div>
                <Snowflake className="w-6 h-6 md:w-10 md:h-10 text-blue-400" />
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Priority Queue */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lead List */}
        <div className="lg:col-span-2">
          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardHeader className="border-b border-emerald-800/30 pb-4">
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <Star className="w-5 h-5 text-emerald-400" />
                Top 10 Leads by AI Score
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {leads.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 text-emerald-800 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No leads yet</h3>
                  <p className="text-emerald-300/70">Add leads to see AI-powered scoring</p>
                </div>
              ) : (
                <div className="divide-y divide-emerald-800/20">
                  {leads.map((lead, index) => {
                    const config = PRIORITY_CONFIG[lead.priority] || PRIORITY_CONFIG.cold;
                    const PriorityIcon = config.icon;
                    const { strokeDashoffset, color } = getScoreRing(lead.overall_score);
                    
                    return (
                      <motion.div
                        key={lead.lead_id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className={`p-4 hover:bg-emerald-900/20 cursor-pointer transition-colors ${
                          selectedLead?.lead_id === lead.lead_id ? 'bg-emerald-900/30' : ''
                        }`}
                        onClick={() => setSelectedLead(lead)}
                        data-testid={`lead-queue-item-${index}`}
                      >
                        <div className="flex items-center gap-4">
                          {/* Rank */}
                          <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-sm">
                            {index + 1}
                          </div>
                          
                          {/* Score Ring */}
                          <div className="relative w-12 h-12 flex-shrink-0 hidden md:flex">
                            <svg className="w-12 h-12 transform -rotate-90">
                              <circle
                                cx="24"
                                cy="24"
                                r="20"
                                stroke="#1e3a2f"
                                strokeWidth="4"
                                fill="none"
                              />
                              <circle
                                cx="24"
                                cy="24"
                                r="20"
                                stroke={color}
                                strokeWidth="4"
                                fill="none"
                                strokeDasharray={2 * Math.PI * 20}
                                strokeDashoffset={(2 * Math.PI * 20) - (lead.overall_score / 100) * (2 * Math.PI * 20)}
                                strokeLinecap="round"
                              />
                            </svg>
                            <span className={`absolute inset-0 flex items-center justify-center text-xs font-bold ${getScoreColor(lead.overall_score)}`}>
                              {lead.overall_score}
                            </span>
                          </div>
                          
                          {/* Lead Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="text-white font-medium truncate">{lead.lead_name}</h4>
                              <Badge className={`${config.color} text-xs`}>
                                <PriorityIcon className="w-3 h-3 mr-1" />
                                {config.label}
                              </Badge>
                            </div>
                            <p className="text-emerald-300/70 text-sm truncate">{lead.lead_email}</p>
                            {lead.company && (
                              <p className="text-emerald-300/50 text-xs flex items-center gap-1 mt-1">
                                <Building className="w-3 h-3" />
                                {lead.company}
                              </p>
                            )}
                          </div>
                          
                          {/* Mobile Score */}
                          <div className="md:hidden flex flex-col items-center">
                            <span className={`text-lg font-bold ${getScoreColor(lead.overall_score)}`}>
                              {lead.overall_score}
                            </span>
                            <span className="text-xs text-emerald-300/50">score</span>
                          </div>
                          
                          {/* Quick Actions */}
                          <div className="hidden md:flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-emerald-400 hover:bg-emerald-900/30"
                              onClick={(e) => { e.stopPropagation(); handleSendEmail(lead); }}
                              title="Send Email"
                            >
                              <Mail className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-blue-400 hover:bg-blue-900/30"
                              onClick={(e) => { e.stopPropagation(); handleBookMeeting(lead); }}
                              title="Book Meeting"
                            >
                              <Calendar className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-purple-400 hover:bg-purple-900/30"
                              onClick={(e) => { e.stopPropagation(); handleAddToCampaign(lead); }}
                              title="Add to Campaign"
                            >
                              <Zap className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          <ChevronRight className="w-5 h-5 text-emerald-600 hidden md:block" />
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Lead Detail Panel */}
        <div className="lg:col-span-1">
          {selectedLead ? (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              key={selectedLead.lead_id}
            >
              <Card className="bg-[#052e22]/50 border-emerald-800/30 sticky top-6">
                <CardHeader className="border-b border-emerald-800/30 pb-4">
                  <CardTitle className="text-lg font-semibold text-white">
                    AI Insights
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {/* Overall Score */}
                  <div className="text-center mb-6">
                    <div className="relative w-24 h-24 mx-auto mb-3">
                      <svg className="w-24 h-24 transform -rotate-90">
                        <circle cx="48" cy="48" r="40" stroke="#1e3a2f" strokeWidth="8" fill="none" />
                        <circle
                          cx="48"
                          cy="48"
                          r="40"
                          stroke={getScoreRing(selectedLead.overall_score).color}
                          strokeWidth="8"
                          fill="none"
                          strokeDasharray={2 * Math.PI * 40}
                          strokeDashoffset={(2 * Math.PI * 40) - (selectedLead.overall_score / 100) * (2 * Math.PI * 40)}
                          strokeLinecap="round"
                        />
                      </svg>
                      <span className={`absolute inset-0 flex items-center justify-center text-2xl font-bold ${getScoreColor(selectedLead.overall_score)}`}>
                        {selectedLead.overall_score}
                      </span>
                    </div>
                    <h3 className="text-white font-semibold">{selectedLead.lead_name}</h3>
                    <p className="text-emerald-300/70 text-sm">{selectedLead.lead_email}</p>
                  </div>

                  {/* Score Breakdown */}
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between items-center">
                      <span className="text-emerald-300/70 text-sm">Engagement</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-emerald-900/50 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-emerald-500 rounded-full" 
                            style={{ width: `${selectedLead.engagement_score}%` }}
                          />
                        </div>
                        <span className="text-white text-sm font-medium w-8">{selectedLead.engagement_score}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-emerald-300/70 text-sm">Intent</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-emerald-900/50 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500 rounded-full" 
                            style={{ width: `${selectedLead.intent_score}%` }}
                          />
                        </div>
                        <span className="text-white text-sm font-medium w-8">{selectedLead.intent_score}</span>
                      </div>
                    </div>
                  </div>

                  {/* Score Explanation */}
                  <div className="p-4 bg-[#0a3d2e] rounded-lg mb-4">
                    <div className="flex items-start gap-2 mb-2">
                      <Lightbulb className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-emerald-200">{selectedLead.score_explanation}</p>
                    </div>
                  </div>

                  {/* Recommendation */}
                  <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg mb-4">
                    <p className="text-xs text-emerald-400 font-medium mb-1">RECOMMENDED ACTION</p>
                    <p className="text-white font-medium mb-2">{selectedLead.recommended_action}</p>
                    <p className="text-sm text-emerald-300/70">{selectedLead.action_reason}</p>
                  </div>

                  {/* Follow Up Time */}
                  <div className="flex items-center gap-2 p-3 bg-[#0a3d2e] rounded-lg mb-6">
                    <Clock className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm text-emerald-300">Best follow-up:</span>
                    <span className="text-white font-medium">{selectedLead.best_follow_up_time}</span>
                  </div>

                  {/* Actions */}
                  <div className="space-y-2">
                    <Button
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => handleSendEmail(selectedLead)}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Send Email
                    </Button>
                    <Button
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => handleBookMeeting(selectedLead)}
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Book Meeting
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full border-emerald-800/50 text-emerald-400 hover:bg-emerald-900/30"
                      onClick={() => handleAddToCampaign(selectedLead)}
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      Add to Campaign
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            <Card className="bg-[#052e22]/30 border-emerald-800/30 border-dashed">
              <CardContent className="py-16 text-center">
                <Target className="w-16 h-16 text-emerald-800 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Select a Lead</h3>
                <p className="text-emerald-300/70">Click on a lead to see AI insights and recommendations</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadPriorityQueue;
